import type { AllComponents } from "../../../../components/_default/interfaces/base";
import { getComponentFromStructureMap } from "../../../../components/_default/utilities/componentIdentity";
import {
  findActiveLanguageByCode,
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "../../../../utils/languageCode";
import type { Language } from "../languageSettings/languageSettingsApi";
import type { StructureMap } from "../structure/structureReducer";
import type { Template } from "./templateApi";

/** `selectedValue`: `"all"` or a site language code (same as `template.languageCode` / Pages filter). */
export function templateMatchesLanguageFilter(
  template: Template,
  selectedValue: unknown
): boolean {
  if (selectedValue === "all" || selectedValue == null || selectedValue === "") {
    return true;
  }
  if (typeof selectedValue !== "string" || !selectedValue.trim()) {
    return false;
  }
  const tplCode = template.languageCode;
  if (typeof tplCode !== "string" || !tplCode.trim()) {
    return false;
  }
  return languageCodesEqual(tplCode, selectedValue);
}

type TemplateLinkMatchRow = {
  rootComponentUuid?: string | null;
};

/** Match link/mega-menu `template_id` (legacy numeric id or root / webpage component UUID). */
export function templateMatchesLinkStoredId(
  t: TemplateLinkMatchRow,
  stored: number | string | null | undefined
): boolean {
  if (stored === null || stored === undefined) return false;
  const key = String(stored).trim();
  if (!key) return false;
  const root = t.rootComponentUuid?.trim();
  if (root && root === key) return true;
  return false;
}

/** Rows that have a non-empty canonical site code (V2 may omit nested `language`). */
export function activeLanguagesForTemplateForms(
  raw: Language[] | undefined | null
): Language[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (row) =>
      row != null &&
      String(languageCodeFromActiveLanguage(row)).trim() !== ""
  ) as Language[];
}

/** Select `value` / payload `languageCode`: canonical code matching API + {@link languageCodeFromActiveLanguage}. */
export function resolveTemplateFormLanguageSelectValue(
  template: Pick<Template, "languageCode"> | null | undefined,
  languages: Language[]
): string {
  if (!languages.length) return "";
  const row = findActiveLanguageByCode(languages, template?.languageCode);
  if (row) return languageCodeFromActiveLanguage(row);
  const raw = template?.languageCode?.trim();
  if (raw) return raw;
  return languageCodeFromActiveLanguage(languages[0]) ?? "";
}

/** Numeric row id for the template's `languageCode`, for UI that resolves flags from `activeLanguages`. */
export function getTemplateDisplayLanguageId(
  template: Template,
  activeLanguages: Language[]
): number | undefined {
  return findActiveLanguageByCode(activeLanguages, template.languageCode)?.id;
}

/**
 * addExistingComponent returns the RTK action `{ payload: StructureMap }` from dispatch;
 * normalize to the structure map for reading `components`.
 */
export function getStructureMapFromAttachDispatch(x: unknown): StructureMap | null {
  if (!x || typeof x !== "object") return null;
  const withPayload = x as { payload?: StructureMap };
  if (withPayload.payload?.components && typeof withPayload.payload.components === "object") {
    return withPayload.payload;
  }
  const direct = x as StructureMap;
  if (direct.components && typeof direct.components === "object") {
    return direct;
  }
  return null;
}

function toTemplateChildren(source: any): any[] {
  if (Array.isArray(source?.childElements)) {
    return source.childElements;
  }
  if (Array.isArray(source?.child_elements)) {
    return source.child_elements;
  }
  return [];
}

function isEditorSentinelComponent(component: any): boolean {
  if (!component || typeof component !== "object") return false;
  return component.id === -11 || component.type === "editor";
}

function isEditorSentinelRelation(relation: any): boolean {
  if (!relation || typeof relation !== "object") return false;
  return relation.child_id === -11;
}

function buildTemplateNodeIndex(
  source: any,
  index: Map<string, any>,
  visited: Set<any>
): void {
  if (!source || typeof source !== "object" || visited.has(source)) {
    return;
  }
  if (isEditorSentinelComponent(source)) {
    return;
  }
  visited.add(source);
  const sourceUuid =
    typeof source.uuid === "string" ? source.uuid.trim() : "";
  if (sourceUuid) {
    index.set(sourceUuid, source);
  }
  for (const child of toTemplateChildren(source).filter(
    (x) => !isEditorSentinelComponent(x)
  )) {
    buildTemplateNodeIndex(child, index, visited);
  }
  if (source.components && typeof source.components === "object") {
    Object.values(source.components).forEach((child) => {
      buildTemplateNodeIndex(child, index, visited);
    });
  }
}

function getTemplateTreeWeight(source: any): number {
  if (!source || typeof source !== "object") {
    return -1;
  }
  const children = toTemplateChildren(source);
  return children.reduce(
    (sum, child) => sum + 1 + Math.max(getTemplateTreeWeight(child), 0),
    0
  );
}

function getTemplateRootFromComponentsMap(template: Template): AllComponents | null {
  const componentsMap =
    template.components && typeof template.components === "object"
      ? (template.components as Record<string, AllComponents>)
      : null;
  if (!componentsMap || Object.keys(componentsMap).length === 0) {
    return null;
  }

  const byUuid = new Map<string, AllComponents>();
  const childUuids = new Set<string>();
  for (const component of Object.values(componentsMap)) {
    if (!component) continue;
    const uuid = component.uuid?.trim();
    if (uuid) {
      byUuid.set(uuid, component);
    }
    for (const relation of component.childWebpageComponentRelations ?? []) {
      const childUuid =
        typeof relation?.child_uuid === "string" ? relation.child_uuid.trim() : "";
      if (childUuid) {
        childUuids.add(childUuid);
      }
    }
  }

  return byUuid.get(template.rootComponentUuid?.trim() ?? "") ?? null;
}

function normalizeTemplateTree(source: any): any {
  if (!source || typeof source !== "object") {
    return source;
  }
  const index = new Map<string, any>();
  buildTemplateNodeIndex(source, index, new Set<any>());
  const visitedUuids = new Set<string>();

  const hydrate = (node: any): any => {
    if (!node || typeof node !== "object") {
      return node;
    }
    const nodeUuid = typeof node.uuid === "string" ? node.uuid.trim() : "";
    if (nodeUuid && visitedUuids.has(nodeUuid)) {
      return { ...node, childElements: [] };
    }
    if (nodeUuid) {
      visitedUuids.add(nodeUuid);
    }

    const directChildren = toTemplateChildren(node).filter(
      (x) => !isEditorSentinelComponent(x)
    );
    const relations = Array.isArray(node.childWebpageComponentRelations)
      ? node.childWebpageComponentRelations
      : [];
    const filteredRelations = relations.filter(
      (relation) => !isEditorSentinelRelation(relation)
    );
    const relationChildren =
      directChildren.length > 0
        ? []
        : filteredRelations
            .map((relation) => {
              const childUuid =
                typeof relation?.child_uuid === "string"
                  ? relation.child_uuid.trim()
                  : "";
              if (!childUuid) {
                return null;
              }
              return index.get(childUuid) ?? null;
            })
            .filter((x): x is any => !!x);
    const resolvedChildren = (directChildren.length > 0
      ? directChildren
      : relationChildren
    ).map((child) => hydrate(child));

    return {
      ...node,
      childElements: resolvedChildren,
      childWebpageComponentRelations:
        filteredRelations.length > 0
          ? filteredRelations
          : resolvedChildren.map((child, index) => ({
              id: 0,
              parent_id: node.id ?? null,
              parent_uuid: node.uuid?.trim() ?? null,
              child_id: child?.id ?? null,
              child_uuid: child?.uuid?.trim() ?? null,
              sort: index + 1,
              created_at: null,
              updated_at: null,
            })),
    };
  };

  const normalized = hydrate(source);
  const childElements = toTemplateChildren(normalized).map((child) =>
    normalizeTemplateTree(child)
  );
  return {
    ...normalized,
    childElements,
    childWebpageComponentRelations:
      Array.isArray(normalized.childWebpageComponentRelations) &&
      normalized.childWebpageComponentRelations.length > 0
        ? normalized.childWebpageComponentRelations
        : childElements.map((child, index) => ({
            id: 0,
            parent_id: normalized.id ?? null,
            parent_uuid: normalized.uuid?.trim() ?? null,
            child_id: child?.id ?? null,
            child_uuid: child?.uuid?.trim() ?? null,
            sort: index + 1,
            created_at: null,
            updated_at: null,
          })),
  };
}

export function getTemplateSourceComponent(
  template: Template | null | undefined
): AllComponents | null {
  if (!template) return null;
  const mapRootSource = getTemplateRootFromComponentsMap(template);
  const componentSource = template.component;
  const preferred =
    getTemplateTreeWeight(mapRootSource) >= getTemplateTreeWeight(componentSource)
      ? mapRootSource
      : componentSource;
  if (!preferred) {
    return null;
  }
  const sourceWithMap =
    mapRootSource && preferred === mapRootSource
      ? { ...preferred, components: template.components ?? {} }
      : preferred;
  const hydrated = normalizeTemplateTree(sourceWithMap) as AllComponents;
  return hydrated;
}

export function getTemplateListKey(template: Template): string {
  if (template.rootComponentUuid?.trim()) {
    return template.rootComponentUuid;
  }
  const sourceComponent = getTemplateSourceComponent(template);
  if (sourceComponent?.uuid?.trim()) {
    return sourceComponent.uuid;
  }
  return template.name;
}

/** True when this template is open in the template edit overlay (matches `editor.template`). */
export function isTemplateActiveInOverlayEditor(
  template: Template,
  editorTemplate: AllComponents | null | false | undefined
): boolean {
  if (!editorTemplate) {
    return false;
  }
  const source = getTemplateSourceComponent(template);
  if (!source) {
    return false;
  }
  const eu = editorTemplate.uuid?.trim();
  const su = source.uuid?.trim();
  if (eu && su && eu === su) {
    return true;
  }
  if (
    editorTemplate.id != null &&
    source.id != null &&
    editorTemplate.id === source.id
  ) {
    return true;
  }
  return false;
}

export function getTemplateWebpageComponentKey(
  template: Template | null | undefined
): string | null {
  if (!template) return null;
  const sourceComponent = getTemplateSourceComponent(template);
  if (sourceComponent?.uuid?.trim()) {
    return sourceComponent.uuid;
  }
  return null;
}

export function getTemplateLinkedComponent(
  components: { [key: string]: AllComponents } | undefined,
  template: Template | null | undefined
): AllComponents | undefined {
  if (!components || !template) return undefined;
  const sourceComponent = getTemplateSourceComponent(template);
  const key = getTemplateWebpageComponentKey(template);
  if (key && components[key]) {
    return components[key];
  }
  if (sourceComponent?.uuid && components[sourceComponent.uuid]) {
    return components[sourceComponent.uuid];
  }
  return undefined;
}

export function getPayloadComponentForTemplate(
  payloadComponents: { [key: string]: AllComponents } | undefined,
  template: Template | null | undefined,
  /** Id that was sent to attachComponent — response map is often keyed by numeric id only. */
  attachedNumericId?: number
): AllComponents | undefined {
  if (!payloadComponents || !template) return undefined;
  if (attachedNumericId != null) {
    const byAttach = payloadComponents[String(attachedNumericId)];
    if (byAttach) return byAttach;
  }
  const sourceComponent = getTemplateSourceComponent(template);
  const key = getTemplateWebpageComponentKey(template);
  if (key && payloadComponents[key]) return payloadComponents[key];
  const u = template.rootComponentUuid?.trim() || sourceComponent?.uuid?.trim();
  if (u) {
    for (const c of Object.values(payloadComponents)) {
      if (c?.uuid?.trim() === u) return c;
    }
  }
  return undefined;
}

export function getTemplateAttachNumericId(
  template: Template
): number | undefined {
  const sourceComponent = getTemplateSourceComponent(template);
  if (sourceComponent?.id != null) return sourceComponent.id;
  return undefined;
}

/**
 * Finds a component's numeric `id` in structure when the map key or row is UUID-only.
 */
function findComponentNumericIdByWebpageUuid(
  components: { [key: string]: AllComponents } | undefined,
  uuid: string | null | undefined
): number | undefined {
  if (!components || !uuid?.trim()) return undefined;
  const u = uuid.trim();
  const direct = components[u];
  if (direct?.id != null) return direct.id;
  for (const c of Object.values(components)) {
    if (c?.uuid?.trim() === u && c.id != null) return c.id;
  }
  return undefined;
}

/**
 * Numeric id for `POST .../attachComponent` — never a UUID string; UUIDs are resolved
 * via {@link getTemplateLinkedComponent} or a full structure scan when needed.
 */
export function resolveTemplateAttachId(
  template: Template | null | undefined,
  components: { [key: string]: AllComponents } | undefined
): number | undefined {
  if (!template) return undefined;
  const direct = getTemplateAttachNumericId(template);
  if (direct != null) return direct;
  const linked = getTemplateLinkedComponent(components, template);
  if (linked?.id != null) return linked.id;
  if (template.rootComponentUuid?.trim()) {
    const byTemplateUuid = findComponentNumericIdByWebpageUuid(
      components,
      template.rootComponentUuid
    );
    if (byTemplateUuid != null) return byTemplateUuid;
  }
  const source = getTemplateSourceComponent(template);
  if (source?.uuid?.trim()) {
    const bySourceUuid = findComponentNumericIdByWebpageUuid(components, source.uuid);
    if (bySourceUuid != null) return bySourceUuid;
  }
  return undefined;
}

/**
 * Whether `editor.template` is the same template root as the resolved attach id / template (overlay switch / delete).
 */
export function editorTemplateMatchesAttachResolution(
  editorTemplate: AllComponents,
  attachId: number | undefined,
  template: Template
): boolean {
  if (attachId != null && editorTemplate.id === attachId) return true;
  const w = template.rootComponentUuid?.trim();
  if (w && editorTemplate.uuid?.trim() === w) return true;
  const src = getTemplateSourceComponent(template);
  if (src?.uuid?.trim() && editorTemplate.uuid?.trim() === src.uuid.trim()) return true;
  return false;
}

export function findTemplateForComponent(
  templates: Iterable<Template>,
  component: Pick<AllComponents, "id" | "uuid">
): Template | undefined {
  const list = Array.isArray(templates) ? templates : Array.from(templates);
  return list.find((t) => {
    const sourceComponent = getTemplateSourceComponent(t);
    if (component.uuid?.trim() && t.rootComponentUuid === component.uuid) {
      return true;
    }
    if (sourceComponent?.uuid && component.uuid && sourceComponent.uuid === component.uuid) {
      return true;
    }
    if (sourceComponent?.id != null && sourceComponent.id === component.id) {
      return true;
    }
    return false;
  });
}

export function findTemplateByStoredReference(
  templates: Iterable<Template>,
  storedId: number | string | null | undefined
): Template | undefined {
  if (storedId === null || storedId === undefined) return undefined;
  const list = Array.isArray(templates) ? templates : Array.from(templates);
  const value = String(storedId);
  return list.find((t) => {
    if (t.id != null && String(t.id) === value) return true;
    if (t.rootComponentUuid && t.rootComponentUuid === value) return true;
    return false;
  });
}

export function templateReferencesComponent(
  template: Template,
  componentId: string | number
): boolean {
  const sourceComponent = getTemplateSourceComponent(template);
  const value = String(componentId);
  if (template.rootComponentUuid && template.rootComponentUuid === value) {
    return true;
  }
  if (sourceComponent?.uuid && sourceComponent.uuid === value) return true;
  return false;
}

export function getTemplateMutationKey(
  template: Pick<
    Template,
    "rootComponentUuid"
  >
): string {
  const root = template.rootComponentUuid?.trim();
  if (root) return root;
  throw new Error("Template missing identifier for mutation");
}

export function resolveTemplateEditorComponent(
  template: Template | null | undefined,
  components: { [key: string]: AllComponents } | undefined
): AllComponents | undefined {
  // Editor must point to a live structure component. Returning detached
  // template payloads here can crash renderers that read from structure by id/uuid.
  return getTemplateLinkedComponent(components, template);
}

/** All nodes in `components` reachable from `rootUuid` (BFS), keyed by each node's uuid. */
export function collectTemplateSubtreeComponentsByUuid(
  components: StructureMap["components"],
  rootUuid: string
): Record<string, AllComponents> {
  const root = rootUuid.trim();
  if (!root) {
    return {};
  }
  const out: Record<string, AllComponents> = {};
  const queue: string[] = [root];
  const queued = new Set<string>([root]);

  while (queue.length > 0) {
    const key = queue.shift()!;
    const node = getComponentFromStructureMap(components, key);
    if (!node?.uuid?.trim()) {
      continue;
    }
    const canon = node.uuid.trim();
    if (out[canon]) {
      continue;
    }
    out[canon] = node;

    for (const rel of node.childWebpageComponentRelations ?? []) {
      const cu =
        typeof rel.child_uuid === "string" ? rel.child_uuid.trim() : "";
      if (cu && !queued.has(cu)) {
        queued.add(cu);
        queue.push(cu);
      }
      if (rel.child_id != null) {
        const cid = String(rel.child_id);
        if (!queued.has(cid)) {
          queued.add(cid);
          queue.push(cid);
        }
      }
    }
    for (const ch of node.childElements ?? []) {
      const cu = ch?.uuid?.trim();
      if (cu && !queued.has(cu)) {
        queued.add(cu);
        queue.push(cu);
      }
    }
  }

  return out;
}

function templateRootUuidForMerge(template: Template): string {
  return (
    template.rootComponentUuid?.trim() ||
    getTemplateSourceComponent(template)?.uuid?.trim() ||
    ""
  );
}

/**
 * Copies header/footer template roots from a saved page `structure` into matching
 * `Template` rows so Redux stays aligned with the canvas (inject / edit / save)
 * without requiring a separate GET /templates round-trip.
 */
export function mergeHeaderFooterTemplatesFromStructure(
  templates: Template[],
  structure: StructureMap
): Template[] {
  const comps = structure.components ?? {};
  const updates = new Map<string, Template>();

  for (const rootKey of structure.webpageComponents ?? []) {
    const c = getComponentFromStructureMap(comps, rootKey);
    if (!c || (c.type !== "header" && c.type !== "footer")) {
      continue;
    }
    const u = c.uuid?.trim();
    if (!u) {
      continue;
    }
    const slice = collectTemplateSubtreeComponentsByUuid(comps, u);
    const match = templates.find((t) => {
      const use = (t.use ?? "").trim().toLowerCase();
      if (c.type === "header" && use !== "header") {
        return false;
      }
      if (c.type === "footer" && use !== "footer") {
        return false;
      }
      return templateRootUuidForMerge(t) === u;
    });
    if (!match) {
      continue;
    }
    updates.set(u, {
      ...match,
      rootComponentUuid: u,
      component: c,
      components: slice,
    });
  }

  if (updates.size === 0) {
    return templates;
  }

  return templates.map((t) => {
    const u = templateRootUuidForMerge(t);
    return u && updates.has(u) ? updates.get(u)! : t;
  });
}
